import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { subDays, startOfDay, endOfDay, isValid } from 'date-fns';
import { FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DynamicTable } from 'components';
import { axiosInstance } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { UsageOptions } from 'components/usage/UsageOptions';
import { UsageOverTimeChart } from 'components/usage/UsageOverTimeChart';
import { labelForPath } from 'components/usage/usageBeacon';
import {
  buildCategories,
  seriesFromRows,
  getDateUnits,
  getTZOffset,
  EVENT_TYPES,
  SERIES_LABELS,
} from 'components/usage/usageChartUtil';

// Core series shown by default on the top Activity chart (kept clean).
const CORE_SERIES = [
  { name: 'Logins', keys: ['logins'] },
  { name: 'Activity Notes', keys: ['notes'] },
  { name: 'AI Usage', keys: ['ai_text', 'ai_voice'] },
  { name: 'Data Edits', keys: ['data_edits'] },
];
// Rarer series layered in by the "Show all events" toggle.
const EXTRA_SERIES = [
  { name: 'Record Views', keys: ['views'] },
  { name: 'Deletes', keys: ['deletes'] },
  { name: 'Exports', keys: ['exports'] },
  { name: 'Failed Logins', keys: ['failed_logins'] },
  { name: 'Unauthorized', keys: ['unauthorized'] },
];

const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// Merged Per-Client / Per-User view. "All Users" shows the whole client;
// selecting a specific user narrows every fetch to that user.
export function UsageReportTab({ clientId }) {
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [filterUser, setFilterUser] = useState('All Users');
  const [eventType, setEventType] = useState('ALL');
  const [showAll, setShowAll] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [activity, setActivity] = useState(null);
  const [users, setUsers] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [pageTrend, setPageTrend] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [query, setQuery] = useState({ limit: 100, offset: 0 });
  const [pagesQuery, setPagesQuery] = useState({ limit: 100, offset: 0 });

  const userId = filterUser && filterUser !== 'All Users' ? filterUser.id : null;
  const userIsAdmin = !!(filterUser && filterUser !== 'All Users' && filterUser.is_admin);
  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();
  const offset = getTZOffset();
  const dateUnits = getDateUnits(startDate, endDate);

  useEffect(() => {
    if (!clientId) return undefined;
    let alive = true;
    axiosInstance
      .post('/UsageAnalytics', {
        type: 'USAGE_ACTIVITY_OVER_TIME',
        filter: { client_id: clientId, ...(userId ? { user_id: userId, user_is_admin: userIsAdmin } : {}) },
        start: startISO, end: endISO, offset, dateUnits,
      }, { params: { selected_client_id: clientId } })
      .then((r) => { if (alive) setActivity(r.data); })
      .catch(() => { if (alive) setActivity({ rows: [] }); });
    return () => { alive = false; };
  }, [clientId, userId, startISO, endISO, dateUnits, offset]);

  useEffect(() => {
    if (!clientId || userId) { setUsers(null); return undefined; }
    let alive = true;
    axiosInstance
      .post('/UsageAnalytics', {
        type: 'USAGE_USER_SUMMARY',
        filter: { client_id: clientId },
        start: startISO, end: endISO,
      }, { params: { selected_client_id: clientId } })
      .then((r) => { if (alive) setUsers(r.data); })
      .catch(() => { if (alive) setUsers({ rows: [] }); });
    return () => { alive = false; };
  }, [clientId, userId, startISO, endISO]);

  useEffect(() => {
    if (!clientId) return undefined;
    let alive = true;
    const cfg = { params: { selected_client_id: clientId } };
    const filt = { client_id: clientId, ...(userId ? { user_id: userId, user_is_admin: userIsAdmin } : {}) };
    axiosInstance
      .post('/UsageAnalytics', { type: 'USAGE_TOP_PAGES', filter: filt, start: startISO, end: endISO }, cfg)
      .then((r) => { if (alive) setTopPages(r.data); })
      .catch(() => { if (alive) setTopPages({ rows: [] }); });
    axiosInstance
      .post('/UsageAnalytics', { type: 'USAGE_PAGEVIEWS_OVER_TIME', filter: filt, start: startISO, end: endISO, offset, dateUnits }, cfg)
      .then((r) => { if (alive) setPageTrend(r.data); })
      .catch(() => { if (alive) setPageTrend({ rows: [] }); });
    return () => { alive = false; };
  }, [clientId, userId, startISO, endISO, dateUnits, offset]);

  // Admins become selectable in the user picker while "Show internal" is on.
  useEffect(() => {
    if (!showInternal || !clientId) { setAdmins([]); return undefined; }
    let alive = true;
    axiosInstance
      .post('/UsageAnalytics', { type: 'USAGE_ADMINS' }, { params: { selected_client_id: clientId } })
      .then((r) => {
        if (alive) setAdmins(((r.data && r.data.rows) || []).map((u) => ({ ...u, is_admin: true })));
      })
      .catch(() => { if (alive) setAdmins([]); });
    return () => { alive = false; };
  }, [showInternal, clientId]);

  const cats = buildCategories(startDate, endDate, dateUnits);
  const catLabels = cats.map((c) => c.label);
  const rows = activity && activity.rows;

  // Memoized per-key aligned arrays; combine() sums several keys per bucket.
  const keyData = {};
  const getKey = (k) => {
    if (!keyData[k]) keyData[k] = seriesFromRows(rows, cats, k);
    return keyData[k];
  };
  const combine = (keys) => cats.map((_, i) => keys.reduce((s, k) => s + (getKey(k)[i] || 0), 0));
  const sumCol = (k) => (rows || []).reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const dropZeroSeries = (list) => list.filter((s) => s.data.some((v) => v > 0));

  // --- Top Activity chart ---
  let topSeries;
  if (eventType === 'ALL') {
    const defs = showAll ? [...CORE_SERIES, ...EXTRA_SERIES] : CORE_SERIES;
    topSeries = defs.map((d) => ({ name: d.name, data: combine(d.keys) }));
  } else {
    const et = EVENT_TYPES.find((t) => t.value === eventType) || EVENT_TYPES[0];
    const keys = et.keys.length ? et.keys : ['total'];
    topSeries = keys.map((k) => ({ name: SERIES_LABELS[k] || k, data: getKey(k) }));
  }
  // Layer in admin/internal activity as a distinct series when toggled on.
  if (showInternal && !userId) {
    topSeries = [...topSeries, { name: 'Internal (Admin)', data: getKey('internal_activity') }];
  }

  // Event Type control lives on the Activity chart card — it only affects this
  // chart, so it doesn't belong in the global filter bar.
  const activityAction = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <FormControl size='small' style={{ minWidth: 180 }}>
        <InputLabel>Event Type</InputLabel>
        <Select value={eventType} label='Event Type' onChange={(e) => setEventType(e.target.value)}>
          {EVENT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {eventType === 'ALL' && (
        <FormControlLabel
          control={<Switch size='small' checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label='Show all events'
        />
      )}
    </div>
  );

  // --- Dedicated AI chart + token summary ---
  const aiSeries = [
    { name: 'Text', data: getKey('ai_text') },
    { name: 'Voice', data: getKey('ai_voice') },
  ];
  const textTokens = sumCol('ai_text_tokens');
  const voiceTokens = sumCol('ai_voice_tokens');
  const aiSubtitle = `Tokens in range — Text: ${fmt(textTokens)} · Voice: ${fmt(voiceTokens)} · Total: ${fmt(textTokens + voiceTokens)}`;

  // --- Dedicated Data Edits by Entity chart ---
  const editSeries = dropZeroSeries([
    { name: 'Providers', data: getKey('edits_providers') },
    { name: 'Contacts', data: getKey('edits_contacts') },
    { name: 'Locations', data: getKey('edits_locations') },
    { name: 'Markets', data: getKey('edits_markets') },
    { name: 'Labels', data: getKey('edits_labels') },
    { name: 'Organizations', data: getKey('edits_organizations') },
  ]);

  const activeUsersSeries = [{ name: 'Active Users', data: getKey('active_users') }];

  // --- Web analytics (page views + time on site) ---
  const pageRows = (pageTrend && pageTrend.rows) || [];
  const pageViewsSeries = [{ name: 'Page Views', data: seriesFromRows(pageRows, cats, 'page_views') }];
  if (showInternal && !userId) {
    pageViewsSeries.push({ name: 'Internal (Admin)', data: seriesFromRows(pageRows, cats, 'internal_page_views') });
  }
  const totalSessions = Number(pageTrend && pageTrend.sessions) || 0;
  const medianSession = Number(pageTrend && pageTrend.median_session_seconds) || 0;
  const pageSubtitle = `Median time on site: ${fmtDuration(medianSession)} · ${fmt(totalSessions)} sessions`;
  const pageColumns = [
    { label: 'Page', key: 'path', format: (v) => labelForPath(v) },
    { label: 'Path', key: 'path' },
    { label: 'Views', key: 'views' },
    { label: 'Median Time', key: 'median_seconds', format: (v) => fmtDuration(v) },
  ];

  // --- Per-user table (whole-client view only) ---
  const et = EVENT_TYPES.find((t) => t.value === eventType);
  const sortKey = et && et.keys.length === 1 ? et.keys[0] : 'logins';
  const validSortCols = ['logins', 'notes', 'active_days', 'ai_text', 'ai_voice', 'data_edits', 'views'];
  const tableSortKey = validSortCols.includes(sortKey) ? sortKey : 'logins';
  const userRows = (((users && users.rows) || []).slice()).sort(
    (a, b) => (Number(b[tableSortKey]) || 0) - (Number(a[tableSortKey]) || 0)
  );
  const columns = [
    { label: 'User', key: 'last_name', format: (v, row) => `${row.last_name || ''}, ${row.first_name || ''}` },
    { label: 'Email', key: 'email' },
    { label: 'Last Login', key: 'last_login', format: (v) => (v ? DateUtil.formatDateTimeFromDB(v) : '—') },
    { label: 'Logins', key: 'logins' },
    { label: 'Active Days', key: 'active_days' },
    { label: 'Notes', key: 'notes' },
    { label: 'Views', key: 'views' },
    { label: 'AI Text', key: 'ai_text' },
    { label: 'AI Voice', key: 'ai_voice' },
    { label: 'Data Edits', key: 'data_edits' },
  ];

  const chartTitle = userId
    ? `Activity — ${filterUser.first_name} ${filterUser.last_name}`
    : 'Activity Over Time';

  return (
    <div style={{ padding: 24 }}>
      <UsageOptions
        dateRange={{ startDate, endDate }}
        filterUser={filterUser}
        adminOptions={showInternal ? admins : []}
        onChangeUser={(u) => setFilterUser(u || 'All Users')}
        onChangeStartDate={(d) => isValid(d) && setStartDate(startOfDay(d))}
        onChangeEndDate={(d) => isValid(d) && setEndDate(endOfDay(d))}
      />

      <FormControlLabel
        control={(
          <Switch
            size='small'
            checked={showInternal}
            onChange={(e) => {
              const on = e.target.checked;
              setShowInternal(on);
              if (!on && userIsAdmin) setFilterUser('All Users');
            }}
          />
        )}
        label='Show internal (admin) activity'
        title='Client numbers always exclude admins. Turn on to layer internal/support activity onto the charts and to pick an individual admin.'
        style={{ marginLeft: 0 }}
      />

      <div style={{ marginTop: 26 }}>
        <UsageOverTimeChart
          title={chartTitle}
          action={activityAction}
          categories={catLabels}
          series={topSeries}
          type='bar'
          stacked
          integer
        />
      </div>

      <UsageOverTimeChart
        title='AI Usage — Text vs Voice'
        subtitle={aiSubtitle}
        categories={catLabels}
        series={aiSeries}
        type='bar'
        stacked
        integer
      />

      <UsageOverTimeChart
        title='Data Edits by Entity'
        categories={catLabels}
        series={editSeries.length ? editSeries : [{ name: 'Edits', data: getKey('data_edits') }]}
        type='bar'
        stacked
        integer
      />

      {!userId && (
        <UsageOverTimeChart
          title='Active Users Over Time'
          categories={catLabels}
          series={activeUsersSeries}
          type='area'
          integer
        />
      )}

      <UsageOverTimeChart
        title='Page Views Over Time'
        subtitle={pageSubtitle}
        categories={catLabels}
        series={pageViewsSeries}
        type='area'
        integer
      />

      <div style={{ marginTop: 16 }}>
        <DynamicTable
          header='Most Visited Pages'
          columns={pageColumns}
          data={(topPages && topPages.rows) || []}
          query={pagesQuery}
          onQueryChange={setPagesQuery}
          idKey='path'
          noDataMessage='No page views in this range yet'
        />
      </div>

      {!userId && (
        <div style={{ marginTop: 16 }}>
          <DynamicTable
            header='Users'
            columns={columns}
            data={userRows}
            query={query}
            onQueryChange={setQuery}
            idKey='user_id'
            noDataMessage='No user activity in this range'
          />
        </div>
      )}
    </div>
  );
}

UsageReportTab.propTypes = {
  clientId: PropTypes.number,
};
